// TODO: Handle errors

const setActiveCart = async function ({ rootState, dispatch, commit }, permId) {
    // This part is only for sales reps and its only available on the client
    if (this.$cognito.enabled && this.$cognito.isLoggedIn && this.$cognito.isSalesAgent) {
        this.$clientCookies.set('activePermId', permId, {
            secure: process.env.NODE_ENV === 'production',
            expires: 3650, // 10 years
        })
    }

    // If the permId is already the permId that we want to set then we have no reason to do this part
    if (rootState.permId === permId) return

    commit('setPermId', permId, { root: true })

    await dispatch('getCart')
}

const setLeadSource = async function ({ rootState, dispatch, state }, { cartId, leadSource }) {
    const isCartActive = rootState.permId === cartId

    // No reason to call this if the active cart lead source already is the same
    if (isCartActive && state.data?.leadSource === leadSource) return

    const headers = {
        'x-site': this.$sitewideData.sitePrefix,
    }

    if (this.$cognito.enabled) {
        const token = await this.$cognito.getToken()
        if (token) headers.Authorization = `Bearer ${token}`
    }

    await this.$axios.put(
        `${this.$config.platform.apis.cart}${cartId}`,
        { leadSource },
        {
            headers,
        }
    )

    if (isCartActive) await dispatch('getCart')
}

const restoreOriginalCart = async function ({ rootState, dispatch, commit }, options = {}) {
    const { refreshCart = true } = options

    // If we run this then we need to make sure the cookie is removed in all cases
    if (process.client) {
        this.$clientCookies.remove('activePermId')
    } else {
        this.$serverCookies.clear('activePermId')
    }

    // If the permId is already set to the original then we have no reason to do this part
    if (rootState.permId === rootState.originalPermId) return

    commit('setPermId', rootState.originalPermId, { root: true })

    // In some situations we want to control when the getCart is called so we have an option to disable
    if (refreshCart) await dispatch('getCart')
}

const getCart = async function ({ rootState, commit }) {
    try {
        const resp = await this.$axios.get(
            `${this.$config.platform.apis.cart}${rootState.permId}`,
            {
                headers: { 'x-site': this.$sitewideData.sitePrefix },
            }
        )

        commit('setCart', resp.data)
    } catch (error) {
        const err = {
            error,
        }
        if (error.response?.status) err.status = error.response.status
        if (error.response?.statusText) err.statusText = error.response.statusText
        if (error.message) err.message = error.message

        if (this.$log) {
            this.$log.error('CRITICAL ERROR: Cannot get cart', err)
        } else console.error('CRITICAL ERROR: Cannot get cart', err)
    }
}

const clearCart = async function ({ rootState, dispatch }, permId) {
    await this.$axios.delete(`${this.$config.platform.apis.cart}${permId || rootState.permId}`, {
        headers: {
            'x-site': this.$sitewideData.sitePrefix,
        },
    })

    await dispatch('getCart')
}

const updateQty = async function ({ rootState, commit }, { itemId, qty }) {
    const resp = await this.$axios.patch(
        `${this.$config.platform.apis.cart}${rootState.permId}/items/${itemId}`,
        { id: itemId, qty },
        {
            headers: {
                'x-site': this.$sitewideData.sitePrefix,
            },
        }
    )

    commit('setCart', resp.data)
}

const removeItem = async function ({ state, rootState, commit }, itemId) {
    // find the item being removed
    const product = state.data.items.find((item) => {
        return item.itemId === itemId
    })

    const resp = await this.$axios.delete(
        `${this.$config.platform.apis.cart}${rootState.permId}/items/${itemId}`,
        {
            headers: {
                'x-site': this.$sitewideData.sitePrefix,
            },
        }
    )

    commit('setCart', resp.data)

    this.$uiEvents.$emit('removeFromCart', product)
}

const addItem = async function ({ rootState, commit }, item) {
    // If we don't have fitmentData then attempt to add it
    if (!item.fitmentData) item.fitmentData = getFilteredFitmentData(rootState)

    try {
        const resp = await this.$axios.post(
            `${this.$config.platform.apis.cart}${rootState.permId}/items`,
            { productList: item },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )
        commit('setCart', resp.data)
        commit('setLastItem', item)
    } catch (err) {
        // Log the error to Segment
        this.$uiEvents.$emit('errorLog', { errorType: 'Add To Cart: ' + err.response.data.code })
        commit('product/setHasOrderControlError', { value: true }, { root: true })
        throw err.response.data
    }
}

// Installernet makes changes to the record after it has been added
const updateLastItem = async function ({ commit }, productId) {
    await commit('setLastItem', { productId })
}

const saveCart = async function ({ rootState }, { fullName, email }) {
    const formData = {
        name: fullName,
        email,
        url: 'https://' + this.$sitewideData.domain + '/restore-cart/',
    }

    const headers = {
        'x-site': this.$sitewideData.sitePrefix,
    }

    await this.$axios.post(
        `${this.$config.platform.apis.cart}${rootState.permId}/share`,
        formData,
        {
            headers,
        }
    )
}

const restoreCart = async function ({ rootState, dispatch }, { pid, force }) {
    if (force) {
        try {
            await this.$axios.post(
                `${this.$config.platform.apis.cart}${rootState.permId}/abandonedcart`,
                { shortId: pid },
                { headers: { 'x-site': this.$sitewideData.sitePrefix } }
            )
            await dispatch('getCart')
        } catch (err) {
            console.log(err)
        }
    } else {
        try {
            await this.$axios.post(
                `${this.$config.platform.apis.cart}${rootState.permId}/restore`,
                { tempPermId: pid },
                { headers: { 'x-site': this.$sitewideData.sitePrefix } }
            )
            await dispatch('getCart')
        } catch (err) {
            console.log(err)
        }
    }
}

const getWishlist = async function ({ dispatch, commit }) {
    const wishlistId = await dispatch('getWishlistId')
    // If we don't have a wishlist id then we are not logged in
    if (!wishlistId) {
        commit('setWishlist', null)
        return
    }

    const resp = await this.$axios.get(`${this.$config.platform.apis.cart}${wishlistId}`, {
        headers: { 'x-site': this.$sitewideData.sitePrefix },
    })

    commit('setWishlist', resp.data)
}

const addToWishlist = async function ({ dispatch, commit }, item) {
    const wishlistId = await dispatch('getWishlistId')
    if (!wishlistId) return

    const { productId, qty, fitmentData, meta } = item

    try {
        const resp = await this.$axios.post(
            `${this.$config.platform.apis.cart}${wishlistId}/items`,
            {
                productList: {
                    productId,
                    qty,
                    fitmentData,
                    ...(meta && { meta }),
                },
            },
            {
                headers: {
                    'x-site': this.$sitewideData.sitePrefix,
                },
            }
        )

        commit('setWishlist', resp.data)
    } catch (err) {
        throw err.response.data
    }
}

// used to move an item from the cart to the wishlist
// The item in this instance must come from the cart
const moveToWishlist = async function ({ dispatch }, item) {
    const [addToWishlistError, removeCartItemError] = await Promise.all([
        // Add item to wishlist
        dispatch('addToWishlist', item).catch(() => true),

        // Remove item from cart
        dispatch('removeItem', item.itemId).catch(() => true),
    ])

    // if these are falsey they we know they succeded.
    if (!addToWishlistError && !removeCartItemError) return

    // if we were unable to add to the wishlist then add back to cart
    if (addToWishlistError && !removeCartItemError) {
        await dispatch('addItem', item)
    }

    throw new Error('unable to move to item to cart')

    // NOTE: We don't have the information here to remove it from the wishlist because we don't know what the id is,
    // So we would have to iterate over the wishlist in order to find the id of the item in the wishlist

    // // if we were unable to remove from the cart then we should remove it from the wishlist
    // if (!addToWishlistError && removeCartItemError) {
    //     dispatch('removeFromWishlist', item)
    // }
}

// used to move an item from the wishlist to the cart
// the item in this instance comes from the wishlist
const moveToCart = async function ({ dispatch }, item) {
    // Pull props that we care about to make a new item
    const { productId, qty, fitmentData, meta } = item

    const [addCartItemError, removeFromWishlistError] = await Promise.all([
        // Add item to cart
        dispatch('addItem', {
            productId,
            qty,
            fitmentData,
            ...(meta && { meta }),
        }).catch(() => true),

        // Remove item from wishlist
        dispatch('removeFromWishlist', item.itemId).catch(() => true),
    ])

    // if these are falsey they we know they succeded.
    if (!addCartItemError && !removeFromWishlistError) return

    // if we were unable to add to the cart then add back to wishlist
    if (addCartItemError && !removeFromWishlistError) {
        await dispatch('addToWishlist', item)
    }

    throw new Error('unable to move to item to cart')

    // if we were unable to remove from the wishlist then we should remove it from the cart
    // if (!addCartItemError && removeFromWishlistError) {
    //     dispatch('removeFromWishlist', item)
    // }
}

const removeFromWishlist = async function ({ dispatch, commit }, itemId) {
    const wishlistId = await dispatch('getWishlistId')
    if (!wishlistId) return

    const resp = await this.$axios.delete(
        `${this.$config.platform.apis.cart}${wishlistId}/items/${itemId}`,
        {
            headers: {
                'x-site': this.$sitewideData.sitePrefix,
            },
        }
    )

    commit('setWishlist', resp.data)
}

const getWishlistId = async function () {
    if (!this.$cognito.enabled) return

    const userAttributes = await this.$cognito.getUserAttributes()

    const userId = userAttributes?.id

    if (!userId) return

    return `${userId}-wishlist`
}

const sendPaylink = async function ({ rootState, dispatch }, { email }) {
    const headers = {
        'x-site': this.$sitewideData.sitePrefix,
    }

    if (this.$cognito.enabled) {
        const token = await this.$cognito.getToken()
        if (token) headers.Authorization = `Bearer ${token}`
    }

    await this.$axios.post(
        `${this.$config.platform.apis.cart}${rootState.permId}/paylink`,
        {
            email,
        },
        {
            headers,
        }
    )
}

export default {
    setActiveCart,
    setLeadSource,
    restoreOriginalCart,
    getCart,
    saveCart,
    restoreCart,
    updateQty,
    removeItem,
    addItem,
    updateLastItem,
    clearCart,
    getWishlistId,
    getWishlist,
    addToWishlist,
    removeFromWishlist,
    moveToWishlist,
    moveToCart,
    sendPaylink,
}

// when we add something to the cart we want to also add fitment that's appropriate for the given product line (we don't want to tell the cart Bed = 'Blah' for a mud flap)
// makeSlug => makeName
function getFilteredFitmentData(rootState) {
    const ocDrills = rootState.product.controls.drillConfig.fitment
    const fitment = rootState.fitment.data
    const obj = {}
    ocDrills.forEach((type) => {
        obj[type.replace('Slug', 'Name')] = fitment[type.replace('Slug', '')]
    })

    return obj
}
